import React, { useMemo } from 'react';
import { Pie } from '@ant-design/plots';
import { Typography } from 'antd';
import './style/DonutChart.css';

const { Title } = Typography;

interface DonutChartProps {
    title: string;
    data: { type: string; value: number }[];
    colors: string[];
    style?: React.CSSProperties;
}

const DonutChart: React.FC<DonutChartProps> = ({ title, data, colors, style }) => {
    const total = useMemo(() => data.reduce((acc, item) => acc + item.value, 0), [data]);

    const config = useMemo(() => ({
        appendPadding: 0,
        data,
        angleField: 'value',
        colorField: 'type',
        radius: 1,
        innerRadius: 0.5,
        scale: {
            color: {
                range: colors,
            },
        },
        legend: false,
        label: false,
        interactions: [{ type: 'element-selected' }, { type: 'element-active' }],
        tooltip: (datum: Record<string, any>) => {
            return {
                name: datum.name || datum.type || 'Без названия',
                value: datum.value,
            };
        },
        statistic: {
            title: false as const,
            content: {
                style: {
                    whiteSpace: 'pre-wrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '32px',
                    lineHeight: '38px',
                    color: '#4D4E65',
                },
                content: `${total}`,
            },
        },
    }), [data, colors, total]);

    const legendItems = useMemo(() =>
        data.map((item, idx) => {
            const percent = total > 0 ? Math.round((item.value / total) * 100) : 0;
            return (
                <div className="donut-chart-legend-item" key={`${item.type}-${item.value}`} style={{ flexDirection: 'column', alignItems: 'flex-start', display: 'flex' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span className="donut-chart-legend-color" style={{ backgroundColor: colors[idx] }} />
                        <span className="donut-chart-legend-label">{item.type}</span>
                    </div>
                    <span className="donut-chart-legend-value" style={{ marginLeft: 24, fontSize: 14, color: '#888' }}>{item.value} устр. - {percent} %</span>
                </div>
            );
        }), [data, colors, total]);

    return (
        <div className="donut-chart-container" style={style}>
            <Title level={5} className="donut-chart-title" style={{ textAlign: 'left', width: '100%' }}>{title}</Title>
            <div className="donut-chart-flex" style={{ minHeight: 0, alignItems: 'center' }}>
                <div
                    className="donut-chart-pie"
                    style={{
                        width: '100%',
                        maxWidth: 350,
                        height: 'auto',
                        aspectRatio: '1/1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Pie {...config} style={{ width: '100%', height: '100%' }} />
                </div>
                <div
                    className="donut-chart-legend"
                    style={{
                        marginLeft: 50,
                        minWidth: 120,
                        gap: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        maxHeight: 350,
                        alignSelf: 'center',
                    }}
                >
                    {legendItems}
                </div>
            </div>
        </div>
    );
};

export default React.memo(DonutChart); 